import type { ConversationPriority, ConversationRow } from '../../services/conversations';
import { usePatchConversationTriage } from '../../hooks/usePatchConversationTriage';
import { PriorityPicker } from '../work-queue/priority/PriorityPicker';
import { AiMarkedControl } from '../monitoring/messages/triage/AiSetMarker';
import { FrustrationPicker } from '../monitoring/messages/triage/FrustrationPicker';

// What the picker shows for a thread nobody has rated yet.
const UNRATED_PRIORITY: ConversationPriority = 'normal';

interface TriageCellProps {
    row: ConversationRow;
    // Opens the menu upwards for rows near the bottom of the clipped scroll body.
    dropUp: boolean;
}

// Components, not render callbacks: DataTable calls `render` as a plain function, and these need hooks.
export function PriorityCell({ row, dropUp }: TriageCellProps) {
    const { mutate } = usePatchConversationTriage();
    return (
        <AiMarkedControl byAi={row.priorityByAi} placement="corner">
            <PriorityPicker
                heading="Priority"
                compact
                requireExtremeReason={false}
                value={row.priority ?? UNRATED_PRIORITY}
                onSelect={(priority) => mutate({ row, patch: { priority } })}
                dropUp={dropUp}
                ariaLabel={`Priority for ${row.name}`}
            />
        </AiMarkedControl>
    );
}

export function FrustrationCell({ row, dropUp }: TriageCellProps) {
    const { mutate } = usePatchConversationTriage();
    return (
        <AiMarkedControl byAi={row.frustrationByAi} placement="corner">
            <FrustrationPicker
                value={row.frustration}
                compact
                // No "Not set" row: the API can't clear a rating, so the null branch never fires.
                allowUnset={false}
                onSelect={(frustration) => {
                    if (frustration) mutate({ row, patch: { frustration } });
                }}
                dropUp={dropUp}
                ariaLabel={`Frustration for ${row.name}`}
            />
        </AiMarkedControl>
    );
}
